import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import { selectStatusesByDomain } from '../../redux/slices/statusDefinitionsSlice';

// Wraps a label onto two lines near its midpoint word boundary, mirroring
// the old hand-written SHORT_LABELS — but derived from whatever label is in
// the DB right now, not a hardcoded per-key map.
const wrapLabel = (label) => {
  const words = label.split(' ');
  if (words.length < 2) return label;
  let bestIdx = 1;
  let bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const left = words.slice(0, i).join(' ').length;
    const right = words.slice(i).join(' ').length;
    const diff = Math.abs(left - right);
    if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
  }
  return `${words.slice(0, bestIdx).join(' ')}\n${words.slice(bestIdx).join(' ')}`;
};

const ShipmentTracker = ({ status, domain = 'shipment' }) => {
  const statuses = useSelector(selectStatusesByDomain(domain));
  const STEPS = statuses.map((s) => ({ key: s.key, label: wrapLabel(s.label) }));
  const currentIdx = STEPS.findIndex((s) => s.key === status);

  if (!STEPS.length) return null;

  return (
    <Box sx={{ position: 'relative', px: 1 }}>
      <Box sx={{
        position: 'absolute',
        top: 13, left: 'calc(5% + 13px)', right: 'calc(5% + 13px)',
        height: 2, bgcolor: 'divider', zIndex: 0,
      }} />
      <Box sx={{
        position: 'absolute',
        top: 13, left: 'calc(5% + 13px)',
        height: 2, bgcolor: 'primary.main', zIndex: 1,
        width: currentIdx <= 0 ? 0
          : `calc(${(currentIdx / (STEPS.length - 1)) * 100}% - ${(currentIdx / (STEPS.length - 1)) * 26}px)`,
        transition: 'width 0.4s ease',
      }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
        {STEPS.map((step, idx) => {
          const done = idx < currentIdx;
          const current = idx === currentIdx;

          return (
            <Tooltip key={step.key} title={step.label.replace('\n', ' ')} placement="top">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, flex: 1 }}>
                <Box sx={{
                  width: 26, height: 26, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: done ? 'primary.main' : current ? 'white' : 'background.default',
                  border: '2px solid',
                  borderColor: done || current ? 'primary.main' : 'divider',
                  color: done ? 'white' : current ? 'primary.main' : 'text.disabled',
                  fontSize: 11, fontWeight: 700,
                  boxShadow: current ? '0 0 0 4px rgba(13,98,155,0.12)' : 'none',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                }}>
                  {done ? <CheckIcon sx={{ fontSize: 14 }} /> : idx + 1}
                </Box>
                <Typography sx={{
                  fontSize: 9.5,
                  fontWeight: current ? 700 : done ? 500 : 400,
                  color: done ? 'primary.main' : current ? 'text.primary' : 'text.disabled',
                  textAlign: 'center',
                  lineHeight: 1.3,
                  whiteSpace: 'pre-line',
                  display: { xs: 'none', sm: 'block' },
                }}>
                  {step.label}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default ShipmentTracker;
